import { createTextGeometry as createGeometry } from '../../../../vendors/three-bmfont-text';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { FontsStorage } from '../init/FontsStorage';
import THREE from '../utils/threeClassesAdapter';

const getShiftValign = (valign) => {
    let shift = 0;

    switch (valign) {
        case 'top': {
            shift -= 1;

            break;
        }

        case 'bottom': {
            shift = 0;

            break;
        }

        default: {
            shift -= 0.5;

            break;
        }
    }

    return shift;
};

class Text3D extends THREE.Group {
    #mesh;
    #material;
    #width = 1;

    constructor(config, text, webgl) {
        super();

        const { width } = config;

        this.config = config;
        this.text = text;
        this.webgl = webgl;
        this.#width = width;

        const fontBmf = this._getFont();

        if (fontBmf) {
            const fontInfoSize = Math.abs(fontBmf.font.info.size);

            const geom = this._getGeometry({ ...config, width }, fontBmf, fontInfoSize);
            const matrl = this._getMaterial(fontBmf, geom.layout, fontInfoSize);
            const mesh = new THREE.Mesh(geom, matrl);

            this.#mesh = mesh;
            this.#material = matrl;

            this.add(mesh);
            this._updatePosition(config, fontInfoSize);
        }
    }

    _getFont(fontFamily2) {
        const { fontFamily } = this.config;

        return Text3D._getFont2(fontFamily2 || fontFamily);
    }

    static _getFont2(fontFamily2) {
        return FontsStorage.get(fontFamily2);
    }

    static getFontScaleRatio(fontFamily2) {
        const fontBmf = Text3D._getFont2(fontFamily2);

        if (!fontBmf) {
            console.warn(`There's no bmfont ${fontFamily2} loaded`);

            return Number.NaN;
        }

        const { fontSizeFix } = fontBmf;
        const fontInfoSize = Math.abs(fontBmf.font.info.size);

        return 1 / (fontInfoSize * fontSizeFix);
    }

    _getGeometry(config, fontBmf, fontInfoSize) {
        const {
            fontSize, lineHeight, letterSpacing, tabSize, align, isUpperCase, isLowerCase, width
        } = config;
        let { text } = this;

        const { fontSizeFix } = fontBmf;
        const scaleFont = fontSize / (fontInfoSize * fontSizeFix);

        // console.log(scaleFont, width, width / scaleFont, fontBmf.font);

        const params = {};

        if (typeof lineHeight !== 'undefined') params.lineHeight = lineHeight * fontInfoSize;
        if (typeof letterSpacing !== 'undefined') params.letterSpacing = letterSpacing * fontInfoSize;
        if (typeof tabSize !== 'undefined') params.tabSize = tabSize;

        if (isUpperCase) text = text.toUpperCase();
        if (isLowerCase) text = text.toLowerCase();

        return createGeometry({
            font: fontBmf.font,
            text,
            width: width / scaleFont,
            flipY: fontBmf.atlas.flipY,
            align: align ?? 'center',
            ...params
        });
    }

    _getMaterial(fontBmf, layout) {
        const { valign } = this.config;

        return MaterialsFactory.create(
            {
                type: 'textMaterial2',
                ...this.config,
                atlas: fontBmf?.atlas,
                translate: new THREE.Vector3(
                    (-layout.width / 2),
                    (-layout.height * getShiftValign(valign) - layout.lineHeight * 0.1),
                    0
                )
            }
        );
    }

    _updatePosition(config, fontInfoSize) {
        if (!this.#mesh) return;

        const { fontSize } = config;

        const scale = this.webgl.getTextScale(
            fontInfoSize,
            fontSize
        );

        this.#mesh.scale.set(scale, -scale, scale);
        this._scale = scale;

        if (this.#material.uniforms.bendingRadius) {
            this.#material.uniforms.bendingRadius.value = config.bendingRadius / scale;
            this.#material.uniformsNeedUpdate = true;
        }
    }

    set bendingRadius(value) {
        if (this.#material.uniforms.bendingRadius) return;

        this.#material.uniforms.bendingRadius.value = value / this._scale;
        this.#material.uniformsNeedUpdate = true;
    }

    get bendingRadius() {
        return this.#material.uniforms.opacity.value;
    }

    set opacity(value) {
        this.#material.uniforms.opacity.value = value;
        this.#material.uniformsNeedUpdate = true;
    }

    get opacity() {
        return this.#material.uniforms.opacity.value;
    }

    set color(value) {
        this.#material.uniforms.uColor.value = value;
        this.#material.uniformsNeedUpdate = true;
    }

    get color() {
        return this.#material.uniforms.uColor.value;
    }

    set width(value) {
        if (value === this.#width) return this.#width;

        // Update width if static variant won't work

        this.#width = value;

        return this.#width;
    }

    get width() {
        return this.#width;
    }

    get mesh() {
        return this.#mesh;
    }

    get layout() {
        if (this.#mesh) return this.#mesh.geometry.layout;

        return null;
    }

    get lineHeight() {
        return this.layout ? this.layout.lineHeight * this._scale : 0;
    }

    get textHeight() {
        return this.layout ? this.layout.height * this._scale : 0;
    }

    get textWidth() {
        return this.layout ? this.layout.width * this._scale : 0;
    }

    get textInnerWidth() {
        if (!this.layout) return 0;

        const { visibleGlyphs } = this.#mesh.geometry;

        visibleGlyphs.sort((a, b) => a.position[0] - b.position[0]);

        const first = visibleGlyphs[0];
        const last = visibleGlyphs[visibleGlyphs.length - 1];

        return (last.position[0]
            + last.data.xoffset
            + last.data.width * 0.5
            - first.position[0]
            - first.data.xoffset
            + first.data.width * 0.5) * this._scale;
    }

    get material() {
        return this.#material;
    }

    setMetrics(m) {
        if (!this.#mesh) return;

        const config = {
            ...this.config,
            ...m
        };

        const fontBmf = this._getFont();
        const fontInfoSize = Math.abs(fontBmf.font.info.size);

        this.#width = config.width;

        const geometry = this._getGeometry(config, fontBmf, fontInfoSize);
        const material = this._getMaterial(fontBmf, geometry.layout, fontInfoSize);

        material.uniforms.opacity.value = this.#material.uniforms.opacity.value;

        this.#mesh.geometry = geometry;
        this.#mesh.material = material;
        this.#material = material;
        this._updatePosition(config, fontInfoSize);
        this.config = config;
    }
}

export default Text3D;
